import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service'; // NotificationService;

/**
 * Displays two embedded Rhombus Systems live camera streams and provides utility to generate and copy public streaming links for an individual camera or all cameras.
 * @component
 * @selector app-camera
 * @example
 *   <app-camera></app-camera>
 * @input None - This component does not accept any @Input properties.
 * @output None - This component does not emit any @Output events.
 *
 * Description:
 *   The CameraComponent embeds two live camera streams using sanitized SafeResourceUrl instances and exposes a utility method to generate public streaming links (for a single camera or a comma-separated list of cameras). It uses DomSanitizer to bypass Angular security for trusted iframe URLs and NotificationService to surface success/error notifications to the user. The Router is injected for potential navigation use, though it is not used directly in the current implementation.
 *
 * Key public properties:
 *   - cameraUrl: SafeResourceUrl - Sanitized URL for the first camera embed.
 *   - camera2Url: SafeResourceUrl - Sanitized URL for the second camera embed.
 *   - camera1Id: string - Static ID for camera 1.
 *   - camera2Id: string - Static ID for camera 2.
 *
 * Internal/configuration:
 *   - camera1UrlString, camera2UrlString (private readonly): raw share URLs for the two cameras.
 *   - cameraIds (private readonly): mapping of numeric camera indexes to camera IDs for link generation.
 *
 * Key methods:
 *   - generatePublicLink(cameraNumber?: number): void
 *       Generates a public streaming URL. If cameraNumber is provided, it generates a link for that specific camera using the corresponding ID; otherwise it generates a link containing all camera IDs (comma-separated). The method copies the generated link to the clipboard and uses NotificationService to show success or error messages. If a requested camera ID is not found, it shows an error message and aborts.
 *
 * Lifecycle and initialization:
 *   - constructor: Injects DomSanitizer, Router, and NotificationService. It initializes and sanitizes the camera embed URLs so they are safe to bind to iframe src attributes. No additional Angular lifecycle hooks (ngOnInit, ngOnDestroy, etc.) are implemented in this component.
 */
@Component({
  selector: 'app-camera',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './camera.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./camera.component.scss'],
})
export class CameraComponent {
  private readonly camera1UrlString =
    'https://console.rhombussystems.com/share/live/Q3LEQ3AXTvSTSq0OPgkuug';
  private readonly camera2UrlString =
    'https://console.rhombussystems.com/share/live/CQ_B5KSISw-VLTyW0eh03Q';

  cameraUrl: SafeResourceUrl;
  camera2Url: SafeResourceUrl;

  // Store all camera IDs in variables
  readonly camera1Id: string = 'Q3LEQ3AXTvSTSq0OPgkuug';
  readonly camera2Id: string = 'CQ_B5KSISw-VLTyW0eh03Q';

  // Camera IDs extracted from URLs (for individual camera links)
  private readonly cameraIds: Record<number, string> = {
    1: 'Q3LEQ3AXTvSTSq0OPgkuug',
    2: 'CQ_B5KSISw-VLTyW0eh03Q',
  };

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private messageService: NotificationService
  ) {
    this.cameraUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.camera1UrlString);
    this.camera2Url = this.sanitizer.bypassSecurityTrustResourceUrl(this.camera2UrlString);
  }

  /**
   * Generate a public streaming URL for a specific camera or for all cameras and copy it to the clipboard.
   * @example
   * eneneratePublicLink(1)
   * undefined (no return value; link copied to clipboard or error messages shown)
   * @param {number} cameraNumber - Optional camera index to generate a link for a single camera; if omitted a combined link using this.camera1Id and this.camera2Id is generated.
   * @returns {void} Copies the generated public link to the clipboard and displays success or error messages via messageService.
   **/
  generatePublicLink(cameraNumber?: number): void {
    let publicLink: string;
    const baseUrl = window.location.origin;

    if (cameraNumber) {
      // Generate link for individual camera
      const cameraId = this.cameraIds[cameraNumber];
      if (!cameraId) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Camera ID not found',
          life: 3000,
        });
        return;
      }
      publicLink = `${baseUrl}/public-stream/${cameraId}`;
    } else {
      // Generate link with all camera IDs (comma-separated)
      const allCameraIds = `${this.camera1Id},${this.camera2Id}`;
      publicLink = `${baseUrl}/public-stream/${allCameraIds}`;
    }

    // Copy to clipboard
    navigator.clipboard
      .writeText(publicLink)
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Link Copied',
          detail: 'Public streaming link copied to clipboard!',
          life: 3000,
        });
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to copy link to clipboard',
          life: 3000,
        });
      });
  }
}
